import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { CommitteeService } from '../../../committees/shared/services/committee.service';
import { CreateSrsBallotModel, ICommitteeSearchData, Option, OptionWithCommitteeCode } from '../../shared/models/sr/sr-committee-search-data';
import { DocumentService } from '../../shared/services/document.service';
import { ISimPublication } from '../../shared/models/sr/sim-publication';
import { Router } from '@angular/router';
import { SrsBallotService } from '../../shared/services/srs-ballot.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ToastMessage,BallotTypes } from '../../../../shared/constants/toast-constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalComponent } from '../../../../shared/modal/modal.component';

@Component({
    selector: 'app-srs-create',
    templateUrl: './srs-create.component.html',
    styleUrls: ['./srs-create.component.scss'],
    standalone: false
})
export class SrsCreateComponent {
  subscriptions: Subscription = new Subscription();
  searchForCommittee: ICommitteeSearchData[] = [];
  simPublications: ISimPublication[] = [];
  activeCommittees: string[] = [];
  committeeCode: string = '';
  committeeName: string = '';
  projectManager: string = '';
  projectManagerEmail: string = '';
  isCommitteeSelected: boolean = false;
  isDesignationSelected: boolean = false;
  isDecisionEnabled: boolean = false;
  isCreateBallotEnabled: boolean = false;
  simPublicationId: string = '';

  selectedDatesForm: FormGroup = new FormGroup({});
  ballotForm = new FormGroup({
    committeeCtrl: new FormControl('', Validators.required),
    designationCtrl: new FormControl('', Validators.required)
  });

  options: Option[] = [];
  publicationOptions: OptionWithCommitteeCode[] = [];
  filteredCommitteeOptions: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
  filteredDesignationOptions: BehaviorSubject<OptionWithCommitteeCode[]> = new BehaviorSubject<OptionWithCommitteeCode[]>([]);
  isSelectedCommitteeOption: boolean = false;
  isSelectedDesignationOption: boolean = false;

  createSrsBallotModel: CreateSrsBallotModel = {
    SimPublicationId: '',
    IsBallotAutoCreated: false,
    StartDate: new Date(),
    EndDate: new Date()
  };

  constructor(
    private _toastService: ToastService,
    private _committeeService: CommitteeService,
    private _documentService: DocumentService,
    private _srsBallotService: SrsBallotService,
    private _router: Router,
    private _modalService: NgbModal
  ) {}

  ngOnInit() {
    this.initializeSRSCreation();
    this.ballotForm.get('committeeCtrl')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '', this.options))
    ).subscribe(filteredOptions => {
      this.filteredCommitteeOptions.next(filteredOptions);
    });

    this.ballotForm.get('designationCtrl')!.valueChanges.pipe(
      startWith(''),
      map(value => this._designationFilter(value || '', this.publicationOptions))
    ).subscribe(filteredOptions => {
      this.filteredDesignationOptions.next(filteredOptions);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  initializeSRSCreation() {
    this.getSimPublicationDesignations();
    this.getActiveCommittees();
  }

  getActiveCommittees() {
    this.subscriptions.add(
      this._committeeService.getActiveCommittee().subscribe((response) => {
        this.searchForCommittee = response.content;
        if (response && this.searchForCommittee.length > 0) {
          this.options = this.searchForCommittee.map((committee) => {
            return {
              label: `${committee.committeeCode} - ${committee.committeeName}`,
              value: committee.committeeCode,
            } as Option;
          });
        }
      })
    );
  }

  getSimPublicationDesignations() {
    this.subscriptions.add(
      this._documentService
        .getAgedDocumentsForManualSRSBallot()
        .subscribe((response) => {
          this.simPublications = response.content;
          if (response && this.simPublications.length > 0) {
            this.publicationOptions = this.simPublications.map((publication) => {
              return {
                label: `${publication.datedDesignation} - ${publication.title}`,
                value: publication.datedDesignation,
                committeeCode: publication.maintenanceCommittee,
                simPublicationId: publication.id
              } as OptionWithCommitteeCode;
            });
          }
        })
    );
  }

  committeeSelected(committeeCode: string, fromDesignation: boolean = false) {
    this.isSelectedCommitteeOption = true;
    this.subscriptions.add(
      this._committeeService
        .getCommittee(committeeCode)
        .subscribe((response) => {
          const result = response.content;
          if (result) {
            this.committeeCode = result.committeeCode;
            this.committeeName = result.committeeName;
            this.projectManager = result.committeeProjectManagerName;
            this.projectManagerEmail = result.committeeProjectManagerEmail;
          }
        })
    );

    if (!fromDesignation && this.publicationOptions?.length > 0) {
          this.publicationOptions = this.publicationOptions.filter(option => option.committeeCode === committeeCode);
    }

    if(!fromDesignation) {
      this.ballotForm.get('designationCtrl')?.setValue(null);
    }

    this.toggleDecisionButton();
    this.toggleCreateBallotButton();
  }




  selectedDesignation(selectedDesignation: OptionWithCommitteeCode) {
    const committeeCode = selectedDesignation.committeeCode;
    this.simPublicationId = selectedDesignation.simPublicationId;
    this.isSelectedDesignationOption = true;
    if(committeeCode) {
      this.ballotForm.patchValue({
        committeeCtrl: committeeCode
      });
      this.committeeSelected(committeeCode, true);
    }

    if (this.options?.length > 0) {
      this.options = this.options.filter(option => option.value === committeeCode);
    }
    this.toggleDecisionButton();
    this.toggleCreateBallotButton();
  }

  cancel() {
    this.ballotForm.reset();
  }


  loadingCommitteeOptions() {
    this.filteredCommitteeOptions.next(this.options);
  }

  loadingDesignationOptions(){
    this.filteredDesignationOptions.next(this.publicationOptions);
  }

  private _filter(value: string, options: Option[]): Option[] {
    const filterValue = value.toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(filterValue));
  }

  private _designationFilter(value: string, optionsArray: OptionWithCommitteeCode[]): OptionWithCommitteeCode[] {
    const filterValue = value.toLowerCase();
    return optionsArray.filter((option) =>
      option.label.toLowerCase().includes(filterValue)
    );
  }

  toggleDecisionButton() {
    const committeeCtrlValue = this.ballotForm.get('committeeCtrl')?.value;
    const designationCtrlValue = this.ballotForm.get('designationCtrl')?.value;
    const isSelectedCommitteeOption = this.isSelectedCommitteeOption;
    const isSelectedDesignationOption = this.isSelectedDesignationOption;


    if (this.ballotForm.valid && committeeCtrlValue && designationCtrlValue && isSelectedCommitteeOption && isSelectedDesignationOption) {
      this.isDecisionEnabled = true;
    } else {
      this.isDecisionEnabled = false;
    }
  }

  toggleCreateBallotButton() {
    const committeeCtrlValue = this.ballotForm.get('committeeCtrl')?.value;
    const designationCtrlValue = this.ballotForm.get('designationCtrl')?.value;
    const startDate = this.selectedDatesForm.get('startDateCtrl')?.value;
    const endDate = this.selectedDatesForm.get('endDateCtrl')?.value;
    const isSelectedCommitteeOption = this.isSelectedCommitteeOption;
    const isSelectedDesignationOption = this.isSelectedDesignationOption;

    if (this.ballotForm.valid && committeeCtrlValue && designationCtrlValue && startDate && endDate && isSelectedCommitteeOption && isSelectedDesignationOption) {
      this.isCreateBallotEnabled = true;
    } else {
      this.isCreateBallotEnabled = false;
    }
  }

  handleSelectedDatesChange(form: FormGroup): void {
    this.selectedDatesForm = form;
    this.toggleCreateBallotButton();
  }

  typingForSearchCommittee(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    if(value == '') {
      this.getSimPublicationDesignations();
    }

    this.getActiveCommittees();
    this.isSelectedCommitteeOption = false;
    this.toggleDecisionButton();
    this.toggleCreateBallotButton();
    this.resetCommitteeDetails();
  }


  typingForSearchDesignation(event: Event) {
    this.isSelectedDesignationOption = false;
    this.toggleDecisionButton();
    this.toggleCreateBallotButton();
  }

  resetCommitteeDetails(){
    this.committeeCode = '';
    this.committeeName = '';
    this.projectManager = '';
    this.projectManagerEmail = '';
  }

  redirectToCreateBallotPage(){
    this._router.navigate(['/ballots/create']);
  }

  createSRSBallot(){
    const startDate = this.selectedDatesForm.get('startDateCtrl')?.value;
    const endDate = this.selectedDatesForm.get('endDateCtrl')?.value;
    this.createSrsBallotModel.SimPublicationId = this.simPublicationId;
    this.createSrsBallotModel.StartDate = startDate;
    this.createSrsBallotModel.EndDate = endDate;
    const errorMessage = ToastMessage.errorMessage;
    const ballotType = BallotTypes.SRS;

    try {
      this._srsBallotService.createSRSBallot(this.createSrsBallotModel).subscribe(
        (response) => {

          if (response.success) {
            this._toastService.clearToasts();
            localStorage.setItem('ballotCreated', 'true');
            this._router.navigate(['/ballots']);
          } else {
            this._toastService.showError(
              errorMessage, ballotType
            );
          }
        },
        (error) => {
          this._toastService.showError(
            errorMessage, ballotType
          );
        }
      );
    } catch (error) {
      this._toastService.showError(
        errorMessage, ballotType
      );
    }

  }

  openDecisionModal(){
    const modalRef = this._modalService.open(ModalComponent);
    modalRef.componentInstance.simPublicationId = this.simPublicationId;
  }
}

